/**
 * PromotionalPopup — Iter 191x
 *
 * Storefront-side rendering of the vendor's configured Promotional Pop-Up.
 * Data shape (persisted at settings.promotional_popup):
 *   {
 *     enabled: bool
 *     layout:  'center' | 'lower_right' | 'lower_banner'
 *     action:  {
 *       mode: 'newsletter' | 'button',
 *       email_placeholder, submit_label, disclaimer,
 *       button_label, button_url,
 *     }
 *     content: { headline, body }
 *     display: {
 *       pages: 'any_first' | 'home_only',
 *       timing_mode: 'timer' | 'scroll' | 'immediate',
 *       timer_seconds: number,
 *       scroll_percent: number,
 *       frequency_days: number,
 *       hide_after_signup: bool,
 *       show_on_mobile: bool,
 *     }
 *     style: {
 *       headline_font, headline_color,
 *       body_font, body_color,
 *       background, button_color, button_text_color,
 *       animation: 'fade' | 'slide-up' | 'none',
 *     }
 *   }
 *
 * Frequency: after dismiss OR signup we set a `popup_seen_at` in
 * localStorage; next visit only re-shows once frequency_days have elapsed.
 */
import React, { useEffect, useState } from 'react';
import { X as XIcon } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STORAGE_SEEN_KEY = (sub) => `promotional_popup_seen:${sub || 'site'}`;
const STORAGE_SUBSCRIBED_KEY = (sub) => `promotional_popup_subscribed:${sub || 'site'}`;

function shouldShow(cfg, subdomain) {
  if (!cfg || !cfg.enabled) return false;
  const display = cfg.display || {};

  // Hide after newsletter signup
  if (display.hide_after_signup) {
    try {
      if (localStorage.getItem(STORAGE_SUBSCRIBED_KEY(subdomain))) return false;
    } catch (_) { /* silent */ }
  }

  // Mobile guard
  if (!display.show_on_mobile && typeof window !== 'undefined') {
    if (window.matchMedia('(max-width: 640px)').matches) return false;
  }

  // Frequency guard
  try {
    const seen = localStorage.getItem(STORAGE_SEEN_KEY(subdomain));
    if (seen) {
      const seenAt = new Date(seen).getTime();
      const days = Math.max(0, Number(display.frequency_days) || 30);
      if ((Date.now() - seenAt) / (1000 * 60 * 60 * 24) < days) return false;
    }
  } catch (_) { /* silent */ }

  // Page guard (home only)
  if (display.pages === 'home_only' && typeof window !== 'undefined') {
    const path = window.location.pathname || '';
    // Home is either /site/<sub> exactly, or a custom-domain root path.
    if (!(/\/site\/[^/]+\/?$/.test(path) || path === '/')) return false;
  }

  return true;
}

export default function PromotionalPopup({ popup, subdomain }) {
  const cfg = popup || {};
  const display = cfg.display || {};
  const style   = cfg.style   || {};
  const content = cfg.content || {};
  const action  = cfg.action  || {};
  const mobile  = cfg.mobile  || {};

  // Detect viewport once for mobile overrides.
  const [isMobile, setIsMobile] = useState(() => {
    try { return typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches; }
    catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const listener = (e) => setIsMobile(e.matches);
    mq.addEventListener?.('change', listener);
    return () => mq.removeEventListener?.('change', listener);
  }, []);

  // Resolved styles — mobile fields override desktop when override_enabled + on mobile.
  const useMobile = isMobile && mobile.override_enabled;
  const R = (k) => (useMobile && mobile[k] ? mobile[k] : style[k]);

  const [visible, setVisible]     = useState(false);
  const [email, setEmail]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [thankYou, setThankYou]   = useState(false);

  // Show/timer wiring
  useEffect(() => {
    if (!shouldShow(cfg, subdomain)) return;
    const mode = display.timing_mode || 'timer';

    if (mode === 'immediate') {
      setVisible(true);
      return;
    }
    if (mode === 'scroll') {
      const pct = Math.max(1, Math.min(100, Number(display.scroll_percent) || 25));
      const onScroll = () => {
        const doc = document.documentElement;
        const scrolled = doc.scrollTop / Math.max(1, doc.scrollHeight - doc.clientHeight);
        if (scrolled * 100 >= pct) {
          setVisible(true);
          window.removeEventListener('scroll', onScroll);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }
    // default: timer
    const seconds = Math.max(0, Number(display.timer_seconds) || 5);
    const t = window.setTimeout(() => setVisible(true), seconds * 1000);
    return () => window.clearTimeout(t);
  }, [cfg, subdomain, display.timing_mode, display.timer_seconds, display.scroll_percent]);

  if (!cfg.enabled || !visible) return null;

  const close = () => {
    try { localStorage.setItem(STORAGE_SEEN_KEY(subdomain), new Date().toISOString()); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setSubmitting(true);
    try {
      // Reuse the storefront email-signup endpoint (already configured server-side).
      await axios.post(`${API}/public/sites/${subdomain}/newsletter`, { email });
    } catch (_) { /* silent — collection may not be wired for every site yet */ }
    setSubmitting(false);
    setThankYou(true);
    try {
      localStorage.setItem(STORAGE_SUBSCRIBED_KEY(subdomain), '1');
      localStorage.setItem(STORAGE_SEEN_KEY(subdomain), new Date().toISOString());
    } catch (_) { /* ignore */ }
  };

  // Layout containers
  const layoutClasses = (() => {
    switch (cfg.layout) {
      case 'lower_right':   return 'fixed bottom-6 right-6 z-[9990] w-full max-w-sm';
      case 'lower_banner':  return 'fixed inset-x-0 bottom-0 z-[9990] px-4 pb-4';
      case 'center':
      default:              return 'fixed inset-0 z-[9990] grid place-items-center px-4';
    }
  })();

  const animClass = {
    'fade':     'animate-[pp-fade-in_0.25s_ease-out]',
    'slide-up': 'animate-[pp-slide-up_0.3s_cubic-bezier(0.16,1,0.3,1)]',
    'none':     '',
  }[style.animation || 'fade'];

  const isCenter = (cfg.layout || 'center') === 'center';

  const closeInside = (style.close_location || 'inside') === 'inside';
  const CloseBtn = (
    <button
      onClick={close}
      className={closeInside
        ? 'absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-lg hover:bg-black/10 transition-colors z-10'
        : 'absolute -top-4 -right-4 w-9 h-9 grid place-items-center rounded-full bg-white text-slate-700 shadow-md hover:bg-slate-100 transition-colors z-10'}
      aria-label="Close"
      data-testid="popup-close"
      style={{ color: style.close_color || style.body_color || '#0F172A' }}
    >
      {(style.close_style || 'icon') === 'text'
        ? <span className="text-[11px] font-bold uppercase tracking-wider">Close</span>
        : <XIcon size={14} />}
    </button>
  );

  return (
    <div className={layoutClasses} data-testid="site-promotional-popup">
      {isCenter && (
        <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={close} />
      )}
      <div
        className={`relative w-full ${cfg.layout === 'lower_banner' ? 'max-w-2xl mx-auto' : 'max-w-md'} rounded-2xl shadow-2xl overflow-hidden ${animClass}`}
        style={{ background: style.background || '#FFFFFF' }}
        data-testid="popup-card"
      >
        {CloseBtn}
        <div className="p-8 text-center space-y-3" style={style.color_behind_text ? { background: style.color_behind_text } : undefined}>
          {thankYou ? (
            <>
              <h3 className="text-2xl font-bold" style={{ color: R('headline_color') || '#0F172A', fontFamily: R('headline_font') || 'inherit' }}>
                Thank you!
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: R('body_color') || '#475569', fontFamily: R('body_font') || 'inherit' }}>
                You&rsquo;ve been added to the list. Check your inbox soon.
              </p>
            </>
          ) : (
            <>
              {content.headline && (
                <h3 className="text-2xl font-bold leading-snug" style={{ color: R('headline_color') || '#0F172A', fontFamily: R('headline_font') || 'inherit' }} data-testid="popup-headline">
                  {content.headline}
                </h3>
              )}
              {content.body && (
                <p className="text-sm leading-relaxed" style={{ color: R('body_color') || '#475569', fontFamily: R('body_font') || 'inherit' }} data-testid="popup-body">
                  {content.body}
                </p>
              )}

              {action.mode === 'button' ? (
                <a
                  href={action.button_url || '#'}
                  className="inline-block mt-2 px-6 py-2.5 text-sm font-bold rounded-md transition-opacity hover:opacity-85"
                  style={{ background: R('button_color') || '#0F172A', color: R('button_text_color') || '#FFFFFF', fontFamily: R('button_font') || 'inherit' }}
                  data-testid="popup-cta-button"
                >
                  {action.button_label || 'Learn more'}
                </a>
              ) : (
                <form onSubmit={submitNewsletter} className="mt-2 flex flex-col gap-2 items-stretch">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={action.email_placeholder || 'Email Address'}
                      className="flex-1 px-3 py-2.5 rounded-md border border-slate-300 text-slate-900 text-sm outline-none focus:border-slate-900"
                      data-testid="popup-email-input"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2.5 text-sm font-bold rounded-md disabled:opacity-60 transition-opacity hover:opacity-85"
                      style={{ background: R('button_color') || '#0F172A', color: R('button_text_color') || '#FFFFFF', fontFamily: R('button_font') || 'inherit' }}
                      data-testid="popup-submit"
                    >
                      {submitting ? '…' : (action.submit_label || 'Subscribe')}
                    </button>
                  </div>
                  {action.disclaimer && (
                    <p className="text-[11px]" style={{ color: R('body_color') || '#94A3B8', fontFamily: R('body_font') || 'inherit' }}>
                      {action.disclaimer}
                    </p>
                  )}
                </form>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pp-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes pp-slide-up { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </div>
  );
}
